import { Page, Layout, Card, Text, Select, Box, TextField, Button, DataTable, BlockStack } from '@shopify/polaris';
import CustomBadge from '#client/components/common/CustomBadge/CustomBadge';
import { DeleteIcon } from '@shopify/polaris-icons';
import axios from 'axios';
import { useEffect, useState, useContext } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { queryClient } from '#client/utils/queryClient';
import { SessionContext } from '#client/contexts/SessionContext.jsx';
import { useParams, useNavigate } from 'react-router';
import './InventoryAdjustmentDetail.css';
import { useToast } from '#client/components/common/Toast/ToastContext';
import ActivityLog from '#client/components/ActivityLog';
import InventoryAdjustmentBreadcrumbs from '#client/pages/InventoryAdjustments/InventoryAdjustmentsBreadCrumbs.jsx';
import AddProductForm from './AddProductForm';
import { hasPermission, formatDate } from '#client/utils/helpers';
import NoPermissions from '#client/pages/NoPermissions';
import UnsavedChangesModal from '#client/components/common/UnsavedChangesModal';
import { useBlocker } from 'react-router';

function InventoryAdjustmentDetail() {
    const { id } = useParams();
    const { currentUser } = useContext(SessionContext);
    const [parentRecord, setParentRecord] = useState({ id });
    const { addToast } = useToast();
    const [selectedReason, setSelectedReason] = useState(-1);
    const [reasonCodes, setReasonCodes] = useState([]);
    const [description, setDescription] = useState('');
    const [store, setStore] = useState(currentUser?.activeStore && currentUser?.activeStore.length > 0 ? currentUser?.activeStore : '');
    const [adjustedItems, setAdjustedItems] = useState([]);
    const [validationErrors, setValidationErrors] = useState({});
    const [highlightedSKU, setHighlightedSKU] = useState('');
    const [lastSaved, setLastSaved] = useState(null);
    const [unauthorized, setUnauthorized] = useState(false);
    const [pageHasChanges, setPageHasChanges] = useState(false);
    const [showUnsavedModel, setShowUnsavedModel] = useState(false);
    const navigate = useNavigate();

    // Fetch reason codes from API
    const fetchReasonCodes = async () => {
        const response = await axios.get(`${import.meta.env.VITE_API_URL}/api/app/inventory-adjustments/reason-codes?isActive=true`);
        return response.data.data.reasonCodes;
    };

    const { data: reasonCodesData, isLoading: isLoadingReasonCodes } = useQuery({
        queryKey: ['inventory-adjustment-reason-codes', 'active'],
        queryFn: fetchReasonCodes,
        staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    });

    useEffect(() => {
        if (reasonCodesData) {
            const formatted = reasonCodesData.map((code) => ({
                id: code.id,
                label: code.reasonCode,
                value: code.id,
                step: code.step,
            }));
            formatted.unshift({ id: -1, label: '', value: -1 });
            setReasonCodes(formatted);
        }
    }, [reasonCodesData]);

    // Modal for unsaved changes
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (pageHasChanges) {
                e.preventDefault();
                setShowUnsavedModel(true);
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [pageHasChanges]);

    const blocker = useBlocker(({ currentLocation, nextLocation }) => {
        // Don't block if we're navigating to the same page or if redirecting from /new to /id after initial save.
        return pageHasChanges && currentLocation.pathname !== nextLocation.pathname && !/\/inventory-adjustments\/\d+$/.test(nextLocation.pathname);
    });

    // Handle the blocked navigation
    useEffect(() => {
        if (blocker?.state === 'blocked') {
            setShowUnsavedModel(true);
        }
    }, [blocker]);

    const handleConfirmNavigation = () => {
        setShowUnsavedModel(false);
        if (blocker.state === 'blocked') {
            blocker.proceed();
        }
    };

    const handleCancelNavigation = () => {
        setShowUnsavedModel(false);
        if (blocker.state === 'blocked') {
            blocker.reset();
        }
    };

    const locations = [{ label: '', value: '' }].concat(
        currentUser?.locationIds.map((id, index) => ({
            value: id,
            label: currentUser?.locationNames[index],
        }))
    );

    let lineItemTableCols = [];
    if (['Completed'].includes(parentRecord?.status)) {
        lineItemTableCols = [
            { label: 'ID', key: 'id', sortable: true, type: 'number' },
            { label: 'Product', key: 'displayName', sortable: true, type: 'text' },
            { label: 'SKU', key: 'sku', sortable: true, type: 'text' },
            { label: 'Previous Qty', key: 'available', sortable: true, type: 'text' },
            { label: 'Qty Adjusted', key: 'adjustmentAmount', sortable: false, type: 'number' },
            { label: 'New Qty', key: 'newQty', sortable: false, type: 'number' },
            { label: 'Actions', key: 'actions', sortable: false, type: 'text' },
        ];
    } else {
        lineItemTableCols = [
            { label: 'ID', key: 'id', sortable: true, type: 'number' },
            { label: 'Product', key: 'displayName', sortable: true, type: 'text' },
            { label: 'SKU', key: 'sku', sortable: true, type: 'text' },
            { label: 'Current Qty', key: 'available', sortable: true, type: 'text' },
            { label: 'Qty Adjusted', key: 'adjustmentAmount', sortable: false, type: 'number' },
            { label: 'New Qty', key: 'newQty', sortable: false, type: 'number' },
            { label: 'Actions', key: 'actions', sortable: false, type: 'text' },
        ];
    }

    const handleLineChangeFactory = (sku) => (newAmount) => {
        setAdjustedItems((prev) => {
            let newAdjustedItems = [...prev];
            const safeNewAmount = !isNaN(parseInt(newAmount)) ? parseInt(newAmount) : 0;
            const oldAdjustmentAmount = newAdjustedItems[newAdjustedItems.findIndex((row) => row.sku === sku)].adjustmentAmount;
            const available = newAdjustedItems[newAdjustedItems.findIndex((row) => row.sku === sku)].available;

            // Let the user clear the input field
            if (newAmount === '') {
                newAdjustedItems[newAdjustedItems.findIndex((row) => row.sku === sku)].adjustmentAmount = '';
            } else {
                newAdjustedItems[newAdjustedItems.findIndex((row) => row.sku === sku)].adjustmentAmount = safeNewAmount * -1 <= available ? safeNewAmount : oldAdjustmentAmount;
            }

            return newAdjustedItems;
        });
        setPageHasChanges(true);
    };

    const handleLineDeleteFactory = (sku) => () => {
        setAdjustedItems((prev) => {
            let newAdjustedItems = [...prev];

            // If the SKU has an ID, it needs deleted from the DB, otherwise just remove it
            if (newAdjustedItems.find((row) => row.sku === sku)?.id) {
                newAdjustedItems[newAdjustedItems.findIndex((row) => row.sku === sku)].deleted = true;
            } else {
                newAdjustedItems = prev.filter((row) => row.sku !== sku);
            }
            return newAdjustedItems;
        });
        setPageHasChanges(true);
    };

    /*********** API Actions ***********/
    const { data: parentRecordData, isError: isParentError } = useQuery({
        queryKey: ['inventory-adjustment', id],
        enabled: id !== 'new',
        queryFn: async () => {
            const parentRecordResponse = await axios.get(`${import.meta.env.VITE_API_URL}/api/app/inventory-adjustments/${id}`);
            return parentRecordResponse.data.data;
        },
    });

    const { data: linesData, isError: isLinesError } = useQuery({
        queryKey: ['inventory-adjustment-lines', id],
        enabled: id !== 'new',
        queryFn: async () => {
            const linesResponse = await axios.get(`${import.meta.env.VITE_API_URL}/api/app/inventory-adjustments/${id}/lines`);
            return Array.isArray(linesResponse.data.data) ? linesResponse.data.data : [];
        },
    });

    useEffect(() => {
        if (parentRecordData) {
            setParentRecord(parentRecordData);
            setStore(parentRecordData.location.locationGid.toString());
            setDescription(parentRecordData.description ?? '');
            if (parentRecordData.reasonCodeId) {
                setSelectedReason(parentRecordData.reasonCodeId);
            }
        }
    }, [parentRecordData]);

    useEffect(() => {
        if (linesData) {
            setAdjustedItems(nestedSort(linesData, 'id', 'descending'));
        }
    }, [linesData]);

    useEffect(() => {
        if (isParentError || isLinesError) {
            setUnauthorized(true);
        }
    }, [isParentError, isLinesError]);

    const saveAdjustmentMutation = useMutation({
        mutationFn: async (adjustment) => {
            const response = await axios.post(`${import.meta.env.VITE_API_URL}/api/app/inventory-adjustments/${parentRecord?.id}/save`, adjustment);
            return response.data.data;
        },
        onSuccess: (data) => {
            setParentRecord(data);
            if (data.reasonCodeId) {
                setSelectedReason(data.reasonCodeId);
            }
            if (data.id && id.toLowerCase() === 'new') {
                navigate(`/inventory-adjustments/${data.id}`, { viewTransition: true });
            }
            setLastSaved(new Date());
            setPageHasChanges(false);
            queryClient.invalidateQueries(['inventory-adjustment', id]);
            queryClient.invalidateQueries(['inventory-adjustment-lines', id]);
            queryClient.invalidateQueries('InventoryAdjustment');
        },
        onError: () => {
            addToast({ message: 'Failed to save adjustment.', type: 'error' });
        },
    });

    const submitAdjustmentMutation = useMutation({
        mutationFn: async (adjustmentId) => {
            await axios.post(`${import.meta.env.VITE_API_URL}/api/app/inventory-adjustments/${adjustmentId}/submit`);
            return adjustmentId;
        },
        onSuccess: () => {
            setParentRecord((prev) => ({ ...prev, status: 'Completed', submittedBy: currentUser?.email, submittedAt: new Date() }));
            addToast({ message: 'Adjustment submitted', type: 'success' });
            setLastSaved(new Date());
            queryClient.invalidateQueries(['inventory-adjustment', id]);
            queryClient.invalidateQueries(['inventory-adjustment-lines', id]);
            queryClient.invalidateQueries('InventoryAdjustment');
        },
        onError: (error) => {
            addToast({ message: 'Failed to submit adjustment. ' + error.response?.data?.error?.message, type: 'error' });
        },
    });

    const saveAdjustment = async (toast = true) => {
        const adjustment = { description: description, reasonCodeId: selectedReason, location: store, status: parentRecord?.status ?? 'Draft', lines: adjustedItems };

        const validation = {
            description: !description.length > 0 ? 'Description is required' : '',
            reason: !selectedReason || selectedReason === -1 ? 'Reason is required' : '',
            store: !store.length > 0 ? 'Store is required' : '',
        };
        adjustedItems.forEach((item) => {
            if (item.adjustmentAmount === '' && !item.deleted) {
                validation[item.sku] = { adjustmentAmount: 'Adjustment amount is required' };
            }
        });
        setValidationErrors(validation);
        const isValid =
            !Object.keys(validation)
                .map((key) => validation[key])
                .filter((error) => error.length > 0).length > 0;
        const invalidLines = adjustedItems.filter((line) => line.adjustmentAmount === '' && !line.deleted);

        if (!isValid || invalidLines.length > 0) {
            addToast({ message: 'To save this adjustment, please complete all required fields.', type: 'error' });
            return;
        }

        try {
            const responseData = await saveAdjustmentMutation.mutateAsync(adjustment);
            if (toast) {
                addToast({ message: 'Adjustment draft saved', type: 'success' });
            }
            return responseData;
        } catch (error) {
            return;
        }
    };

    const submitAdjustment = async () => {
        let adjustmentId;
        try {
            const saveResponse = await saveAdjustment(false);
            adjustmentId = saveResponse.id;
        } catch (error) {
            console.error(error);
            return;
        }
        try {
            await submitAdjustmentMutation.mutateAsync(adjustmentId);
            return;
        } catch (error) {
            return;
        }
    };

    const handleSort = (index, direction) => {
        setAdjustedItems(nestedSort(adjustedItems, lineItemTableCols[index].key, direction));
    };

    const dataTableRows = adjustedItems
        .filter((row) => !row.deleted)
        .map((row) =>
            lineItemTableCols.map((field) => {
                let cellClass = '';
                if (row['adjustmentAmount'] >= 0) {
                    cellClass = 'positive';
                } else if (row['adjustmentAmount'] < 0) {
                    cellClass = 'positive'; // change to negative to show net negative adjustments with red bg
                }

                if (field.key === 'newQty') {
                    const newQty = row['adjustmentAmount'] + row['available'];

                    // If submitted, display available quantity in DB from time of submission
                    // Otherwise calculate from live shopify available quantity
                    if (['Completed'].includes(parentRecord?.status)) {
                        return (
                            <div key={field.key} className={highlightedSKU === row['sku'] ? 'row-flash-green' : ''}>
                                <div className={'cell-content ' + cellClass}>
                                    <Text>{row['postAdjustmentQty']}</Text>
                                </div>
                            </div>
                        );
                    } else {
                        return (
                            <div key={field.key} className={highlightedSKU === row['sku'] ? 'row-flash-green' : ''}>
                                <div className={'cell-content ' + cellClass}>
                                    <Text>{!isNaN(newQty) ? newQty : ''}</Text>
                                </div>
                            </div>
                        );
                    }
                } else if (field.key === 'adjustmentAmount') {
                    return (
                        <div key={field.key} className={highlightedSKU === row['sku'] ? 'row-flash-green' : ''}>
                            <div className={'cell-content'}>
                                <TextField
                                    disabled={['Completed'].includes(parentRecord?.status)}
                                    type="number"
                                    align="end"
                                    value={row.adjustmentAmount}
                                    onChange={handleLineChangeFactory(row.sku)}
                                    inputMode="numeric"
                                    autoComplete="off"
                                    autoSize="true"
                                    error={validationErrors[row.sku]?.adjustmentAmount}
                                />
                            </div>
                        </div>
                    );
                } else if (field.key === 'available' && ['Completed'].includes(parentRecord?.status)) {
                    return (
                        <div key={field.key} className={'cell-content'}>
                            <Text>{row['preAdjustmentQty']}</Text>
                        </div>
                    );
                } else if (field.key === 'actions' && !['Completed'].includes(parentRecord?.status)) {
                    return (
                        <div key={field.key} className={highlightedSKU === row['sku'] ? 'row-flash-green' : ''}>
                            <div className={'cell-content'}>
                                <Button icon={DeleteIcon} variant="plain" onClick={handleLineDeleteFactory(row.sku)} accessibilityLabel={`Delete ${row.sku}`} />
                            </div>
                        </div>
                    );
                } else {
                    return (
                        <div key={field.key} className={highlightedSKU === row['sku'] ? 'row-flash-green' : ''}>
                            <div className={'cell-content'}>
                                <Text>{row[field.key] ?? ''}</Text>
                            </div>
                        </div>
                    );
                }
            })
        );
    if (unauthorized) {
        return <NoPermissions />;
    }

    return (
        <div id="inventory-adjustment-detail">
            <InventoryAdjustmentBreadcrumbs adjustmentName={parentRecord?.name ?? 'New'} />
            <Page
                title="Inventory Adjustment"
                fullWidth
                primaryAction={
                    hasPermission(currentUser, 'adjApprove') ? (
                        <Button
                            variant="primary"
                            disabled={['Completed'].includes(parentRecord?.status) || adjustedItems.length === 0}
                            onClick={() => {
                                submitAdjustment();
                            }}
                        >
                            Approve & Submit
                        </Button>
                    ) : null
                }
                secondaryActions={
                    <Button
                        variant="secondary"
                        disabled={['Completed'].includes(parentRecord?.status) || !pageHasChanges}
                        onClick={() => {
                            saveAdjustment();
                        }}
                    >
                        Save Draft
                    </Button>
                }
            >
                {id !== 'new' && (
                    <Box paddingBlockEnd="200" paddingBlockStart="200">
                        <Card>
                            <Layout>
                                <Layout.Section>
                                    <BlockStack gap="200">
                                        <Box style={{}}>
                                            <Text variant="headingLg">{parentRecord?.name}</Text>
                                        </Box>
                                        <Box style={{}}>
                                            <Text variant="headingSm" as="span">
                                                Status:{' '}
                                            </Text>
                                            <span>
                                                <CustomBadge key={`badge-${parentRecord?.name}`} status={parentRecord?.status} />
                                            </span>
                                        </Box>

                                        {parentRecord?.status === 'Completed' && (
                                            <>
                                                <Box style={{}}>
                                                    <Text variant="headingSm" as="span">
                                                        Reason Code:{' '}
                                                    </Text>
                                                    <span>{reasonCodes.find((rc) => rc.id === parentRecord?.reasonCodeId)?.label}</span>
                                                </Box>
                                                <Box style={{}}>
                                                    <Text variant="headingSm" as="span">
                                                        Location:{' '}
                                                    </Text>
                                                    <span>{parentRecord?.location?.name}</span>
                                                </Box>
                                                <Box style={{}}>
                                                    <Text variant="headingSm" as="span">
                                                        Description:{' '}
                                                    </Text>
                                                    <span>{parentRecord?.description}</span>
                                                </Box>
                                                <Box style={{}}>
                                                    <Text variant="headingSm" as="span">
                                                        Submitted By:{' '}
                                                    </Text>
                                                    <span>
                                                        {parentRecord?.submittedBy} ({formatDate(parentRecord?.submittedAt)})
                                                    </span>
                                                </Box>
                                            </>
                                        )}

                                        <Box style={{}}>
                                            <Text variant="headingSm" as="span">
                                                Created By:{' '}
                                            </Text>
                                            <span>
                                                {parentRecord?.createdBy} ({formatDate(parentRecord?.createdAt)})
                                            </span>
                                        </Box>
                                    </BlockStack>
                                </Layout.Section>
                            </Layout>
                        </Card>
                    </Box>
                )}
                {parentRecord?.status !== 'Completed' && (
                    <Box paddingBlockEnd="200" paddingBlockStart="200">
                        <Card>
                            <Layout>
                                <Layout.Section>
                                    <Box paddingBlockEnd="100" paddingBlockStart="100" maxWidth="300px">
                                        <Select
                                            label="Reason Code"
                                            disabled={['Completed'].includes(parentRecord?.status) || isLoadingReasonCodes}
                                            options={reasonCodes}
                                            onChange={(r) => {
                                                setSelectedReason(parseInt(r));
                                                setPageHasChanges(true);
                                            }}
                                            value={selectedReason}
                                            error={validationErrors.reason}
                                        />
                                    </Box>
                                    <Box paddingBlockEnd="100" paddingBlockStart="100" maxWidth="300px">
                                        <Select
                                            disabled={adjustedItems.length > 0 || ['Completed'].includes(parentRecord?.status)}
                                            label="Store"
                                            options={locations}
                                            onChange={(value) => {
                                                setStore(value);
                                                setPageHasChanges(true);
                                            }}
                                            value={store || ''}
                                            error={validationErrors.store}
                                        />
                                    </Box>
                                    <Box paddingBlockEnd="100" paddingBlockStart="100" maxWidth="300px">
                                        <TextField
                                            label="Description"
                                            disabled={['Completed'].includes(parentRecord?.status)}
                                            maxLength={100}
                                            onChange={(value) => {
                                                setDescription(value);
                                                setPageHasChanges(true);
                                            }}
                                            value={description}
                                            error={validationErrors.description}
                                            autoComplete="off"
                                            showCharacterCount
                                        />
                                    </Box>
                                </Layout.Section>
                            </Layout>
                        </Card>
                    </Box>
                )}

                <AddProductForm
                    step={reasonCodes.find((r) => r.value === selectedReason)?.step ?? 1}
                    locationId={store}
                    isHidden={['Completed'].includes(parentRecord?.status)}
                    onSubmit={(newProduct) => {
                        if (isNaN(parseInt(newProduct.available, 10))) {
                            addToast({ message: 'Product is not stocked at selected location.', type: 'error' });
                            return;
                        }
                        const existing = adjustedItems.find((item) => item.sku === newProduct.sku);

                        // Check if new adjustment produces a valid inventory
                        const outOfInventoryMessage = 'Available inventory is negative and cannot be adjusted downwards.';

                        if (existing && newProduct.adjustmentAmount < 0 && newProduct.available + existing.adjustmentAmount + newProduct.adjustmentAmount < 0) {
                            addToast({ message: outOfInventoryMessage, type: 'error' });
                            return;
                        } else if (!existing && newProduct.adjustmentAmount < 0 && newProduct.available + newProduct.adjustmentAmount < 0) {
                            addToast({ message: outOfInventoryMessage, type: 'error' });
                            return;
                        }

                        setAdjustedItems((prev) => {
                            const existing = prev.find((item) => item.sku === newProduct.sku);

                            if (existing) {
                                return [
                                    {
                                        ...existing,
                                        adjustmentAmount: (parseInt(existing.adjustmentAmount, 10) || 0) + (parseInt(newProduct.adjustmentAmount, 10) || 0),
                                        sku: newProduct.sku,
                                        displayName: newProduct.displayName,
                                        available: newProduct.available,
                                        variantId: newProduct.variantId,
                                    },
                                    ...adjustedItems.filter((i) => i.sku !== existing.sku),
                                ];
                            } else {
                                return [
                                    {
                                        adjustmentAmount: parseInt(newProduct.adjustmentAmount, 10) || 0,
                                        sku: newProduct.sku,
                                        displayName: newProduct.displayName,
                                        available: newProduct.available,
                                        variantId: newProduct.variantId,
                                    },
                                    ...adjustedItems,
                                ];
                            }
                        });
                        addToast({
                            type: 'success',
                            message: `Product added: ${newProduct.displayName}`,
                        });
                        setPageHasChanges(true);
                        setHighlightedSKU(newProduct.sku);
                        setTimeout(() => {
                            setHighlightedSKU('');
                        }, 500);
                    }}
                />

                <Box paddingBlockEnd="200" paddingBlockStart="200">
                    <Card>
                        <Layout>
                            <Layout.Section>
                                <Box paddingBlockEnd="100" paddingBlockStart="100">
                                    <Box paddingBlockEnd="500">
                                        <Text variant="headingMd">Adjusted Items</Text>
                                    </Box>

                                    <DataTable
                                        columnContentTypes={lineItemTableCols.map((field) => field.type)}
                                        headings={lineItemTableCols.map((field) => field.label)}
                                        rows={nestedSort(dataTableRows, lineItemTableCols[2].key, 'ascending')}
                                        sortable={lineItemTableCols.map((field) => field.sortable)}
                                        defaultSortDirection="descending"
                                        onSort={handleSort}
                                        initialSortColumnIndex={0}
                                    />
                                </Box>
                            </Layout.Section>
                        </Layout>
                    </Card>
                </Box>
                {id !== 'new' && (
                    <Box paddingBlockEnd="200" paddingBlockStart="200">
                        <Card>
                            <Layout>
                                <Layout.Section>
                                    <Box paddingBlockEnd="100" paddingBlockStart="100">
                                        <ActivityLog objectType="InventoryAdjustment" objectId={parentRecord?.name} key={lastSaved} />
                                    </Box>
                                </Layout.Section>
                            </Layout>
                        </Card>
                    </Box>
                )}
                <UnsavedChangesModal onConfirm={handleConfirmNavigation} onClose={handleCancelNavigation} open={showUnsavedModel} />
            </Page>
        </div>
    );
}

function nestedSort(rows, key, direction) {
    const sortedRows = [...rows];
    sortedRows.sort((a, b) => {
        const aValue = a[key];
        const bValue = b[key];
        if (aValue < bValue) {
            return direction === 'ascending' ? -1 : 1;
        }
        if (aValue > bValue) {
            return direction === 'ascending' ? 1 : -1;
        }
        return 0;
    });
    return sortedRows;
}

export default InventoryAdjustmentDetail;
